// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToChainAfter } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    DispatchResetOrderApprovals,
    SendTracking,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import { xGoogleAnalyticsState } from "@steiner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@steiner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xSystemListState } from "@steiner/App/Store/Reducers";
import { isSchneiderProduct } from "@steiner/App/Store/Selectors/SystemListSelector";
import { getCategoryLevels } from "@steiner/XDL/Utilities";

export const XngGaSendTracking: typeof DispatchResetOrderApprovals = props => {
    const state = props.getState() as xGoogleAnalyticsState & xSystemListState;
    const updatedCart = { ...props.cartToUpdate, orderNumber: props.apiResult.cart.orderNumber };
    const session = getSession(props.getState());

    if (state.xGoogleAnalytics.purchaseEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }
        const { apiResult } = props;
        const ecommerceItems: any[] = [];
        const ecommerceSchniderItems: any[] = [];
        let schneiderTotal = 0;
        updatedCart.cartLines?.forEach((cartLine, idx) => {
            const item = {
                item_id: cartLine.erpNumber,
                item_name: cartLine.shortDescription,
                affiliation: apiResult.billTo?.companyName,
                coupon: apiResult.cart.promotionCode,
                discount: cartLine.pricing
                    ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                        ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                        : 0
                    : 0,
                index: idx,
                item_brand: state.xGoogleAnalytics.includeProductBrand ? cartLine.brand?.name || "" : undefined,
                ...(state.xGoogleAnalytics.includeProductCategory ? getCategoryLevels(cartLine.properties) : {}),
                item_list_id: state.xGoogleAnalytics.includeProductCategory
                    ? cartLine.properties?.["categoryId"] || ""
                    : undefined,
                locationId: session.pickUpWarehouse?.id,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                isSchneider: isSchneiderProduct(state.xSystemList, cartLine.brand?.name),
            };
            ecommerceItems.push(item);
            if (item.isSchneider) {
                ecommerceSchniderItems.push(item);
                schneiderTotal += cartLine.pricing?.extendedActualPrice || 0;
            }
        });

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.purchaseEventName,
            ecommerce: {
                transcation_id: apiResult.cart.orderNumber,
                value: apiResult.cart.orderSubTotal,
                tax: apiResult.cart.totalTax,
                shipping: apiResult.cart.shippingAndHandling,
                currency: session.currency?.currencyCode,
                coupon: apiResult.cart.promotionCode,
                affiliation: apiResult.billTo?.companyName,
                items: ecommerceItems,
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);

        if (ecommerceSchniderItems.length) {
            const googleAnalyticsSchniderData = {
                event: `se_${state.xGoogleAnalytics.purchaseEventName}`,
                ecommerce: {
                    transcation_id: apiResult.cart.orderNumber,
                    value: schneiderTotal,
                    tax: apiResult.cart.totalTax,
                    shipping: apiResult.cart.shippingAndHandling,
                    currency: session.currency?.currencyCode,
                    coupon: apiResult.cart.promotionCode,
                    affiliation: apiResult.billTo?.companyName,
                    items: ecommerceSchniderItems,
                },
            };
            dataLayer.push(googleAnalyticsSchniderData);
        }
    }
};

addToChainAfter(chain, SendTracking, XngGaSendTracking);
